import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../components/AuthProvider';
import { companyApiService, CompanyProfile } from '../services/companyApi';
import { 
  Building2, 
  Mail, 
  Phone, 
  Globe, 
  MapPin, 
  Save, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  ArrowLeft,
  Shield,
  Upload,
  Image as ImageIcon
} from 'lucide-react';

const CompanyProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState<CompanyProfile>({
    name: 'IRIS SOFTECH',
    slogan: 'Solutions Digitales Innovantes',
    email: 'softechiris@gmail.com',
    phone: '771742350',
    website: 'www.irissoftech.com',
    address: 'Medina RUE 27 X 22 ENSUITE',
    logo: '',
    iris_avatar: '',
    tva_rate: 0
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  // Vérifier si l'utilisateur est admin
  useEffect(() => {
    const checkAdminStatus = () => {
      console.log('🔍 Vérification admin - user:', user);
      const isUserAdmin = user?.isAdmin || false;
      console.log('🔍 isUserAdmin:', isUserAdmin);
      setIsAdmin(isUserAdmin);
      
      // Temporairement, permettre l'accès à tous les utilisateurs connectés
      // TODO: Remettre la vérification admin quand isAdmin sera correctement défini
      /*
      if (!isUserAdmin) {
        console.log('❌ Pas admin, redirection vers /');
        navigate('/');
      } else {
        console.log('✅ Utilisateur admin confirmé');
      }
      */
      console.log('⚠️ Accès temporairement autorisé pour tous les utilisateurs connectés');
    };

    if (user) {
      checkAdminStatus();
      loadProfile();
    }
  }, [navigate, user]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      
      // Essayer de charger depuis l'API
      try {
        const profileData = await companyApiService.getCompanyProfile();
        console.log('📥 Profil chargé depuis le serveur:', profileData);
        
        // Utiliser logo_url si disponible, sinon logo
        const processedProfile = {
          ...profileData,
          logo: profileData.logo_url || profileData.logo || '',
          iris_avatar: profileData.iris_avatar_url || profileData.iris_avatar || ''
        };
        
        setProfile(processedProfile);
      } catch (apiError) {
        console.log('Profil non trouvé sur le serveur, utilisation des valeurs par défaut');
        // Garder les valeurs par défaut si pas de profil sur le serveur
      }
      
    } catch (error) {
      console.error('Erreur lors du chargement du profil:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement du profil' });
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    try {
      setSaving(true);
      setMessage(null);
      
      // Sauvegarder tous les champs au backend, même s'ils sont vides
      await companyApiService.updateCompanyProfile(profile);
      
      setMessage({ type: 'success', text: 'Profil sauvegardé avec succès !' });
      
      // Effacer le message après 3 secondes
      setTimeout(() => setMessage(null), 3000);
      
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof CompanyProfile, value: string) => {
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner un fichier image' });
      return;
    }

    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Le fichier ne doit pas dépasser 5MB' });
      return;
    }

    try {
      setUploadingLogo(true);
      setMessage(null);

      const logoUrl = await companyApiService.uploadLogo(file);
      console.log('🖼️ Logo uploadé, URL reçue:', logoUrl);
      
      // Mettre à jour immédiatement l'affichage avec l'URL reçue
      setProfile(prev => ({
        ...prev,
        logo: logoUrl
      }));

      // Recharger le profil complet depuis le serveur pour s'assurer de la cohérence
      await loadProfile();

      setMessage({ type: 'success', text: 'Logo uploadé avec succès !' });
      setTimeout(() => setMessage(null), 3000);

    } catch (error) {
      console.error('Erreur lors de l\'upload du logo:', error);
      setMessage({ type: 'error', text: 'Erreur lors de l\'upload du logo' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner un fichier image' });
      return;
    }

    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Le fichier ne doit pas dépasser 5MB' });
      return;
    }

    try {
      setUploadingAvatar(true);
      setMessage(null);

      const avatarUrl = await companyApiService.uploadIrisAvatar(file);
      console.log('🤖 Avatar IRIS uploadé, URL reçue:', avatarUrl);
      
      // Mettre à jour immédiatement l'affichage avec l'URL reçue
      setProfile(prev => ({
        ...prev,
        iris_avatar: avatarUrl
      }));

      // Recharger le profil complet depuis le serveur
      await loadProfile();

      setMessage({ type: 'success', text: 'Avatar IRIS uploadé avec succès !' });
      setTimeout(() => setMessage(null), 3000);

    } catch (error) {
      console.error('Erreur lors de l\'upload de l\'avatar IRIS:', error);
      setMessage({ type: 'error', text: 'Erreur lors de l\'upload de l\'avatar IRIS' });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const triggerAvatarInput = () => {
    avatarInputRef.current?.click();
  };


  // Temporairement désactivé - permettre l'accès à tous les utilisateurs connectés
  // TODO: Remettre la vérification admin
  /*
  if (!isAdmin) {
    return null;
  }
  */

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          <span className="text-gray-600 text-lg">Chargement du profil...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header avec navigation */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Retour à l'accueil</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-600">Zone Admin</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* En-tête de la page */}
          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                <Building2 className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Profil de l'Entreprise</h1>
            <p className="text-gray-600 mt-2">Configurez les informations de votre entreprise IRIS SOFTECH</p>
          </div>

          {/* Actions */}
          <div className="flex justify-center">
            <button
              onClick={saveProfile}
              disabled={saving}
              className="px-8 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>{saving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
            </button>
          </div>

          {/* Message de statut */}
          {message && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-4 rounded-lg flex items-center space-x-3 ${
                message.type === 'success' 
                  ? 'bg-green-50 border border-green-200 text-green-800' 
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}
            >
              {message.type === 'success' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
              <span>{message.text}</span>
            </motion.div>
          )}

          {/* Formulaire */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="p-8 space-y-6">
              {/* Upload du logo */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  <ImageIcon className="w-4 h-4 inline mr-2" />
                  Logo de l'entreprise
                </label>
                <div className="flex items-center space-x-4">
                  {/* Aperçu du logo */}
                  <div className="w-20 h-20 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden">
                    {profile.logo ? (
                      <img 
                        src={profile.logo} 
                        alt="Logo entreprise" 
                        className="w-full h-full object-cover rounded-lg"
                        onLoad={() => console.log('✅ Logo chargé dans l\'upload:', profile.logo)}
                        onError={() => console.log('❌ Erreur de chargement du logo dans l\'upload:', profile.logo)}
                      />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-gray-400" />
                    )}
                  </div>
                  
                  {/* Bouton d'upload */}
                  <div className="flex-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={triggerFileInput}
                      disabled={uploadingLogo}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors flex items-center space-x-2"
                    >
                      {uploadingLogo ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      <span>{uploadingLogo ? 'Upload...' : 'Choisir un logo'}</span>
                    </button>
                    <p className="text-xs text-gray-500 mt-1">PNG, JPG jusqu'à 5MB</p>
                  </div>
                </div>
              </div>

              {/* Upload de l'avatar IRIS */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  <ImageIcon className="w-4 h-4 inline mr-2" />
                  Avatar IRIS (Chatbot)
                </label>
                <div className="flex items-center space-x-4">
                  {/* Aperçu de l'avatar */}
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full border-2 border-dashed border-purple-300 flex items-center justify-center overflow-hidden">
                    {profile.iris_avatar ? (
                      <img 
                        src={profile.iris_avatar} 
                        alt="Avatar IRIS" 
                        className="w-full h-full object-cover rounded-full"
                        onLoad={() => console.log('✅ Avatar IRIS chargé:', profile.iris_avatar)}
                        onError={() => console.log('❌ Erreur chargement avatar IRIS:', profile.iris_avatar)}
                      />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-purple-400" />
                    )}
                  </div>
                  
                  {/* Bouton d'upload */}
                  <div className="flex-1">
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarUpload}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={triggerAvatarInput}
                      disabled={uploadingAvatar}
                      className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 disabled:opacity-50 transition-colors flex items-center space-x-2"
                    >
                      {uploadingAvatar ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      <span>{uploadingAvatar ? 'Upload...' : 'Choisir un avatar'}</span>
                    </button>
                    <p className="text-xs text-gray-500 mt-1">PNG, JPG jusqu'à 5MB (utilisé pour le chatbot)</p>
                  </div>
                </div>
              </div>

              {/* Nom de l'entreprise */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  <Building2 className="w-4 h-4 inline mr-2" />
                  Nom de l'entreprise
                </label>
                <input
                  type="text"
                  value={profile.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="Nom de votre entreprise"
                />
              </div>

              {/* Slogan */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  ✨ Slogan
                </label>
                <input
                  type="text"
                  value={profile.slogan}
                  onChange={(e) => handleInputChange('slogan', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="Votre slogan"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email de contact
                </label>
                <input
                  type="email"
                  value={profile.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="contact@exemple.com"
                />
              </div>

              {/* Téléphone */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Numéro de téléphone
                </label>
                <input
                  type="tel"
                  value={profile.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="771742350"
                />
              </div>

              {/* Site web */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  <Globe className="w-4 h-4 inline mr-2" />
                  Site web
                </label>
                <input
                  type="url"
                  value={profile.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="www.exemple.com"
                />
              </div>

              {/* Adresse physique */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  Adresse physique
                </label>
                <textarea
                  value={profile.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none"
                  placeholder="Adresse complète de votre entreprise"
                />
              </div>

              {/* Section Réseaux sociaux */}
              <div className="col-span-2">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  🌐 Réseaux sociaux
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Facebook */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      📘 Facebook
                    </label>
                    <input
                      type="url"
                      value={profile.facebook_url || ''}
                      onChange={(e) => handleInputChange('facebook_url', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="https://facebook.com/votreentreprise"
                    />
                  </div>

                  {/* Twitter */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      🐦 Twitter
                    </label>
                    <input
                      type="url"
                      value={profile.twitter_url || ''}
                      onChange={(e) => handleInputChange('twitter_url', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="https://twitter.com/votreentreprise"
                    />
                  </div>

                  {/* LinkedIn */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      💼 LinkedIn
                    </label>
                    <input
                      type="url"
                      value={profile.linkedin_url || ''}
                      onChange={(e) => handleInputChange('linkedin_url', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="https://linkedin.com/company/votreentreprise"
                    />
                  </div>

                  {/* Instagram */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      📷 Instagram
                    </label>
                    <input
                      type="url"
                      value={profile.instagram_url || ''}
                      onChange={(e) => handleInputChange('instagram_url', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="https://instagram.com/votreentreprise"
                    />
                  </div>
                </div>
              </div>

              {/* Taux de TVA */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  📊 Taux de TVA (%)
                </label>
                <input
                  type="number"
                  value={profile.tva_rate || 0}
                  onChange={(e) => handleInputChange('tva_rate', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="0"
                  min="0"
                  max="100"
                  step="0.01"
                />
                <p className="text-xs text-gray-500 mt-1">Taux de TVA appliqué aux factures (par défaut: 0%)</p>
              </div>
            </div>
          </div>

          {/* Aperçu */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              👁️ Aperçu du profil
            </h3>
            
            <div className="bg-white rounded-xl p-6 border border-blue-100">
              <div className="flex items-start space-x-6">
                <div className="w-20 h-20 bg-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {profile.logo ? (
                    <img 
                      src={profile.logo} 
                      alt="Logo entreprise" 
                      className="w-full h-full object-cover rounded-2xl"
                      onLoad={() => console.log('✅ Logo chargé dans l\'aperçu:', profile.logo)}
                      onError={() => console.log('❌ Erreur de chargement du logo:', profile.logo)}
                    />
                  ) : (
                    <Building2 className="w-10 h-10 text-white" />
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="text-2xl font-bold text-blue-600 mb-2">{profile.name}</h4>
                  <p className="text-gray-600 mb-4 text-lg">{profile.slogan}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-gray-500">
                    <p className="flex items-center space-x-2">
                      <Mail className="w-4 h-4" />
                      <span>{profile.email}</span>
                    </p>
                    <p className="flex items-center space-x-2">
                      <Phone className="w-4 h-4" />
                      <span>{profile.phone}</span>
                    </p>
                    <p className="flex items-center space-x-2">
                      <Globe className="w-4 h-4" />
                      <span>{profile.website}</span>
                    </p>
                    <p className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4" />
                      <span>{profile.address}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CompanyProfilePage;
